import React, {useEffect, useMemo} from 'react';
import {useMap} from 'react-leaflet';
import L from 'leaflet';
import {Surcouche} from './CarteLeaflet.js';
import {RAMPE_POPULATION, rangPalier, seuilsQuantiles} from '../../selectors/population.js';
import {TEXTE, VERDICT_COULEURS} from '../../tokens.js';

// Marquages lot 13 (source osrm-foot seulement) : D8 « détour à pied » = liseré orange (token verdict « problème »),
// D9 « non rattaché » = hachure diagonale (token texte). Aucune couleur de remplissage supplémentaire.
export const COULEUR_DETOUR = VERDICT_COULEURS.pb.texte;
export const COULEUR_NON_RATTACHE = TEXTE;

/*
 * Calque « Densité » (surcouche `densite=1`, lot 12) : un carré de 200 m par carreau INSEE, rempli sur une rampe bleu clair -> navy
 * (quantiles 20/40/60/80 % de `pop`). Canvas Leaflet propre dans un pane sous les limites de communes et le canvas des PAV (même
 * mécanique que la heatmap : dessin direct dans le contexte du renderer, redessiné à chaque `update`). Monté seulement si actif.
 * Source : `snapshot.territoire.carreaux` (INSEE, Filosofi 2019, carreaux 200 m), passés en propriété `carreaux` (lot 13).
 */

const COTE_M = 200;
const OPACITE = 0.6;
const M_PAR_DEGRE_LAT = 111320;

/** Seuils de quantiles de la population des carreaux. */
export const seuilsDensite = (carreaux) => seuilsQuantiles(carreaux.map((c) => c.pop));

/** Hachure diagonale (traits à 45°) dans le rectangle, écrêtée à celui-ci. */
function hachurer(ctx, x, y, w, h) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.beginPath();
    for (let k = -h; k < w; k += 4) {
        ctx.moveTo(x + k, y + h);
        ctx.lineTo(x + k + h, y);
    }
    ctx.stroke();
    ctx.restore();
}

function dessiner(map, renderer, carreaux, seuils, marquages) {
    const ctx = renderer._ctx;
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    const b = renderer._bounds;
    const demiLat = COTE_M / 2 / M_PAR_DEGRE_LAT;
    ctx.save();
    ctx.globalAlpha = OPACITE;
    for (const c of carreaux) {
        const demiLng = demiLat / Math.cos((c.lat * Math.PI) / 180);
        const a = map.latLngToLayerPoint([c.lat + demiLat, c.lng - demiLng]);
        const z = map.latLngToLayerPoint([c.lat - demiLat, c.lng + demiLng]);
        if (z.x < b.min.x || a.x > b.max.x || z.y < b.min.y || a.y > b.max.y) continue;
        ctx.fillStyle = RAMPE_POPULATION[rangPalier(c.pop, seuils)];
        // +0,5 px : pas de filet blanc entre carreaux voisins arrondis à des pixels différents.
        ctx.fillRect(a.x, a.y, z.x - a.x + 0.5, z.y - a.y + 0.5);
        if (marquages?.actif) {
            if (marquages.detours.has(c.index)) {
                ctx.save();
                ctx.globalAlpha = 1;
                ctx.strokeStyle = COULEUR_DETOUR;
                ctx.lineWidth = 2;
                ctx.strokeRect(a.x + 1, a.y + 1, z.x - a.x - 2, z.y - a.y - 2);
                ctx.restore();
            } else if (marquages.nonRattaches.has(c.index)) {
                ctx.save();
                ctx.globalAlpha = 0.85;
                ctx.strokeStyle = COULEUR_NON_RATTACHE;
                ctx.lineWidth = 1;
                hachurer(ctx, a.x, a.y, z.x - a.x, z.y - a.y);
                ctx.restore();
            }
        }
    }
    ctx.restore();
}

export default function CarteDensiteLayer({carreaux, marquages = null}) {
    const map = useMap();
    const seuils = useMemo(() => seuilsDensite(carreaux), [carreaux]);
    useEffect(() => {
        if (!map.getPane('carteDensite')) {
            const pane = map.createPane('carteDensite');
            pane.style.zIndex = 340; // sous les communes (350) et le canvas des PAV (400)
            pane.style.pointerEvents = 'none';
        }
        const renderer = L.canvas({pane: 'carteDensite', padding: 0.5});
        renderer.addTo(map);
        const redessiner = () => dessiner(map, renderer, carreaux, seuils, marquages);
        renderer.on('update', redessiner);
        redessiner();
        return () => {
            renderer.off('update', redessiner);
            map.removeLayer(renderer);
        };
    }, [map, carreaux, seuils, marquages]);
    return null;
}

const nb = (n) => Math.round(n).toLocaleString('fr-FR');

/** Légende du calque Densité (bas gauche) : 5 paliers, bornes en habitants par carreau de 200 m. */
export function LegendeDensite({carreaux, marquages = null, bas = 116}) {
    const libelles = useMemo(() => {
        const s = seuilsDensite(carreaux);
        return [`< ${nb(s[0])}`, `${nb(s[0])} à ${nb(s[1] - 1)}`, `${nb(s[1])} à ${nb(s[2] - 1)}`, `${nb(s[2])} à ${nb(s[3] - 1)}`, `≥ ${nb(s[3])}`];
    }, [carreaux]);
    return (
        <Surcouche
            role="group"
            aria-label="Légende de la densité de population"
            style={{left: 16, bottom: bas, background: 'rgba(255,255,255,.94)', border: '1px solid #D5DAE1', borderRadius: 12, padding: '8px 12px', fontSize: 11, color: '#2D333B', pointerEvents: 'none'}}
        >
            <div style={{fontWeight: 700, marginBottom: 4}}>Population par carreau de 200 m</div>
            {RAMPE_POPULATION.map((couleur, i) => (
                <div key={couleur} style={{display: 'flex', alignItems: 'center', gap: 8, lineHeight: '16px'}}>
                    <span style={{width: 18, height: 10, background: couleur, border: '1px solid rgba(33,48,103,.25)', display: 'inline-block'}}/>
                    {libelles[i]} hab.
                </div>
            ))}
            {marquages?.actif && (
                <>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, lineHeight: '16px', marginTop: 4}}>
                        <span style={{width: 18, height: 10, boxSizing: 'border-box', border: `2px solid ${COULEUR_DETOUR}`, display: 'inline-block'}}/>
                        détour à pied
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, lineHeight: '16px'}}>
                        <span style={{width: 18, height: 10, border: '1px solid rgba(33,48,103,.25)', display: 'inline-block', background: `repeating-linear-gradient(135deg, ${COULEUR_NON_RATTACHE} 0 1px, transparent 1px 4px)`}}/>
                        non rattaché
                    </div>
                </>
            )}
            <div style={{color: '#6B7480', marginTop: 4}}>INSEE, Filosofi 2019 (quantiles 20 / 40 / 60 / 80 %)</div>
        </Surcouche>
    );
}

/** Légende de la chalandise : paliers de population allouée (quantiles des PAV), rayon en mètres. */
export function LegendeChalandise({seuils, rayonM, distance = "à vol d'oiseau"}) {
    const libelles = [`< ${nb(seuils[0])}`, `${nb(seuils[0])} à ${nb(seuils[1] - 1)}`, `${nb(seuils[1])} à ${nb(seuils[2] - 1)}`, `${nb(seuils[2])} à ${nb(seuils[3] - 1)}`, `≥ ${nb(seuils[3])}`];
    return (
        <Surcouche
            role="group"
            aria-label="Légende de la population allouée"
            style={{left: 16, bottom: 116, background: 'rgba(255,255,255,.94)', border: '1px solid #D5DAE1', borderRadius: 12, padding: '8px 12px', fontSize: 11, color: '#2D333B', pointerEvents: 'none'}}
        >
            <div style={{fontWeight: 700, marginBottom: 4}}>Population allouée ({rayonM} m {distance})</div>
            <div style={{color: '#6B7480', marginBottom: 4}}>Parc aérien. Intramuros : maillage de proximité, non représenté</div>
            {RAMPE_POPULATION.map((couleur, i) => (
                <div key={couleur} style={{display: 'flex', alignItems: 'center', gap: 8, lineHeight: '16px'}}>
                    <span style={{width: 18, height: 10, background: couleur, border: '1px solid rgba(33,48,103,.25)', display: 'inline-block'}}/>
                    {libelles[i]} hab.
                </div>
            ))}
        </Surcouche>
    );
}
